import { children, createSignal, onCleanup, Show, type Accessor, type JSX } from "solid-js";
import { Tooltip, type TriggerProps } from "./tooltip";
import { Button } from "./button";
import { LabelText } from "./form";
import clsx from "~/util/html/style";

const COPIED_TIMEOUT_MS = 1500;

type UseCopyReturn = {
  copy: (text: string) => Promise<void>;
  copied: Accessor<boolean>;
};

export function useCopy(): UseCopyReturn {
  const [copied, setCopied] = createSignal(false);
  let timeoutId: number | undefined;

  onCleanup(() => {
    window.clearTimeout(timeoutId);
  });

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);

    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_TIMEOUT_MS);
  };

  return { copy, copied };
}

type CopyableProps = {
  text: string;
  children: (props: {
    copy: () => void;
    copied: Accessor<boolean>;
    text: string;
    tooltipTriggerProps: TriggerProps;
  }) => JSX.Element;
  tooltip?: JSX.Element;
};

export function Copyable(props: CopyableProps) {
  const { copy, copied } = useCopy();

  return (
    <Tooltip enabled={Boolean(props.tooltip)} content={props.tooltip}>
      {(triggerProps) =>
        props.children({
          copy: () => void copy(props.text),
          copied,
          text: props.text,
          tooltipTriggerProps: triggerProps,
        })
      }
    </Tooltip>
  );
}

// Renders `text` as the label by default; pass children to keep the same copy behavior
// with a custom label (multi-line or styled content). The tooltip defaults to the text and
// "Click to copy"; pass your own content to lay it out, or false for none.
export function CopyableText(props: {
  text: string;
  tooltip?: JSX.Element | false;
  class?: string;
  children?: JSX.Element;
}) {
  const label = children(() => props.children);
  const tooltip = () => {
    if (props.tooltip === false) return undefined;
    return props.tooltip ?? `${props.text} - Click to copy`;
  };

  return (
    <Copyable text={props.text} tooltip={tooltip()}>
      {({ copied, copy, text, tooltipTriggerProps }) => (
        <button
          {...tooltipTriggerProps}
          // without this, a copyable value inside a <form> is a submit button — clicking
          // it to copy would submit the enclosing form
          type="button"
          class={clsx(
            "cursor-pointer select-none",
            label() === undefined && "max-w-full truncate",
            props.class,
          )}
          onClick={(e) => {
            e.stopPropagation();
            copy();
          }}
        >
          <Show when={!copied()} fallback="Copied!">
            {label() ?? text}
          </Show>
        </button>
      )}
    </Copyable>
  );
}

const COPY_FIELD_CLASS =
  "w-full resize-none rounded-lg border border-brand-subtle bg-brand-extra-light px-3 py-2.5 font-mono text-xs text-typography-primary outline-none focus-visible:ring-2 focus-visible:ring-brand-focus";

// A read-only, selectable value with a Copy button — for links, embed snippets, IDs, and other
// values people copy verbatim. Pass `rows` to render a multi-line textarea instead of an input.
export function CopyField(props: { value: string; label?: string; rows?: number }) {
  const { copy, copied } = useCopy();

  return (
    <div class="flex flex-col gap-1.5">
      <div class={clsx("flex items-center gap-2", props.label ? "justify-between" : "justify-end")}>
        <Show when={props.label}>{(label) => <LabelText>{label()}</LabelText>}</Show>
        <Button variant="secondary" size="small" onClick={() => void copy(props.value)}>
          {copied() ? "Copied" : "Copy"}
        </Button>
      </div>
      <Show
        when={props.rows}
        fallback={
          <input
            readOnly
            value={props.value}
            onFocus={(e) => e.currentTarget.select()}
            class={COPY_FIELD_CLASS}
          />
        }
      >
        {(rows) => (
          <textarea
            readOnly
            rows={rows()}
            value={props.value}
            onFocus={(e) => e.currentTarget.select()}
            class={COPY_FIELD_CLASS}
          />
        )}
      </Show>
    </div>
  );
}

/** A button that copies `text` and says so, labelled with what it copies. */
export function CopyButton(props: { text: string; label: string }) {
  const { copy, copied } = useCopy();

  return (
    <Button
      variant="secondary"
      size="small"
      leftIconName="clipboard"
      onClick={() => void copy(props.text)}
    >
      {copied() ? "Copied!" : props.label}
    </Button>
  );
}
